import {
  TrackerEventPayload,
  TrackerHistoryPayload,
  TrackerHistoryPayloadAsset,
  TrackerStatus,
} from '@app/modules/client/map/shared/models/tracker-history-payload.model'

export type DemuxKind = 'position' | 'event' | 'fleet' | 'alert' | 'ack' | 'assetPosition' | 'assetEvent' | 'unknown'
export interface DemuxResult {
  kind: DemuxKind
  data: any
}

/** epoch-ms as number OR numeric-string OR ISO string -> number (mitigates Q5). */
export function normalizeTs(ts: unknown): number {
  if (typeof ts === 'number') return ts
  if (typeof ts === 'string') {
    const n = Number(ts)
    if (Number.isFinite(n)) return n
    const parsed = Date.parse(ts)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

/** Classify one inbound WS frame. Alerts have NO `type` key (contract §4). */
export function demuxFrame(raw: unknown): DemuxResult {
  if (!raw || typeof raw !== 'object') return { kind: 'unknown', data: raw }
  const obj = raw as Record<string, any>
  if (typeof obj['action'] === 'string') {
    return { kind: 'ack', data: obj } // 'subscribed' | 'error' | 'unsubscribed'
  }
  const t = obj['type']
  const data = obj['data']
  // asset frames carry a distinctive `data.asset`; route by shape so the exact `type` label
  // (coordinatesByAsset / newSensorHistory — unconfirmed on dev) is not load-bearing.
  const isAssetShape = data && typeof data === 'object' && 'asset' in data
  if (isAssetShape) {
    return 'eventData' in data ? { kind: 'assetEvent', data } : { kind: 'assetPosition', data }
  }
  if (t === 'coordinatesByAsset') return { kind: 'assetPosition', data }
  if (t === 'newSensorHistory') return { kind: 'assetEvent', data }
  if (t === 'position' || t === 'event' || t === 'fleet') {
    return { kind: t, data }
  }
  // flat WebAlert: no `type`, has id + event + status
  if (obj['id'] != null && 'event' in obj && 'status' in obj) {
    return { kind: 'alert', data: obj }
  }
  return { kind: 'unknown', data: obj }
}

/** v3 position `data` -> existing TrackerHistoryPayload; guards null status / empty coords. */
export function normalizePositionFrame(data: any): TrackerHistoryPayload {
  const coordinates = Array.isArray(data?.coordinates)
    ? data.coordinates.map((c: any) => ({
        id: String(c?.id ?? ''),
        ts: normalizeTs(c?.ts),
        lng: Number(c?.lng),
        lat: Number(c?.lat),
      }))
    : []
  const status: TrackerStatus | null = data?.status ?? null
  return { vehicleId: Number(data?.vehicleId), coordinates, status } as unknown as TrackerHistoryPayload
}

/**
 * v3 asset position `data` -> TrackerHistoryPayloadAsset; guards null status / empty coords / NaN coords.
 * Null when no `asset` — the asset identity (`asset.id`) is what every consumer derefs, so an asset-less
 * frame is unconsumable (route() drops it, mirroring normalizeAssetEventFrame).
 */
export function normalizeAssetFrame(data: any): TrackerHistoryPayloadAsset | null {
  if (!data?.asset) return null
  const coordinates = Array.isArray(data?.coordinates)
    ? data.coordinates.map((c: any) => ({
        id: String(c?.id ?? ''),
        ts: normalizeTs(c?.ts),
        lng: Number(c?.lng),
        lat: Number(c?.lat),
      }))
    : []
  const status: TrackerStatus | null = data?.status ?? null
  return { asset: data.asset, deviceId: String(data?.deviceId ?? ''), coordinates, status } as unknown as TrackerHistoryPayloadAsset
}

/** v3 asset sensor event `data` -> TrackerEventPayload; null when no asset (unconsumable). */
export function normalizeAssetEventFrame(data: any): TrackerEventPayload | null {
  if (!data?.asset) return null
  return { asset: data.asset, eventData: data.eventData } as unknown as TrackerEventPayload
}
